/** Default sort used by the questions list screen (client-side only). */
export const QUESTION_LIST_DEFAULTS = {
  size: 10,
  sortBy: 'question',
  sortDir: 'asc',
};

/** Sort keys exposed by the questions table column headers. */
export const QUESTION_SORT_FIELDS = {
  question: 'question',
  correctAnswer: 'correctAnswer',
  mimeType: 'mimeType',
  questionType: 'questionType',
  active: 'active',
};

/**
 * Computes the next client-side sort when a column header is clicked.
 */
export function getNextQuestionSort(columnKey, currentSortBy, currentSortDir) {
  const sortBy = columnKey || QUESTION_LIST_DEFAULTS.sortBy;
  if (currentSortBy === sortBy) {
    return {
      sortBy,
      sortDir: currentSortDir === 'asc' ? 'desc' : 'asc',
    };
  }
  return { sortBy, sortDir: 'asc' };
}

/**
 * Stable cache key for a paginated question list query.
 */
export function buildQuestionListCacheKey(testId, {
  page = 0,
  size = QUESTION_LIST_DEFAULTS.size,
  keyword = '',
} = {}) {
  const kw = (keyword || '').trim();
  return `questions:${testId}:${page}:${size}:${kw}`;
}

/** Builds query string for paginated question list API calls. */
export function buildQuestionListQuery({
  testId,
  page = 0,
  size = 10,
  keyword = '',
} = {}) {
  const params = new URLSearchParams();
  params.set('testId', String(testId));
  params.set('page', String(page));
  params.set('size', String(size));
  if (keyword && keyword.trim()) {
    params.set('keyword', keyword.trim());
  }
  return params.toString();
}

/** Maps raw API question payload to table row shape. */
export function mapQuestionApiRow(item) {
  const answers = (item.answer || []).map((ans) => ({
    answerId: ans.answerId,
    text: ans.answer,
    isCorrect: ans.isCorrect === 1 || ans.is_correct === 1,
  }));
  const correctAnswerObj = answers.find((ans) => ans.isCorrect);
  return {
    id: item.id,
    questionId: item.questionId,
    question: item.question,
    testId: item.testId,
    domain_id: item.domain_id,
    explanation: item.explanation,
    hint: item.hint,
    keywords: item.keywords,
    mimeType: item.mimeType,
    questionType: item.questionType,
    asset: item.asset,
    passage: item.passage,
    caseStudyId: item.caseStudyId,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    isDeleted: item.isDeleted,
    active: item.active,
    answer: answers,
    correctAnswer: correctAnswerObj ? correctAnswerObj.text : null,
  };
}

function questionSortValue(row, sortBy) {
  switch (sortBy) {
    case QUESTION_SORT_FIELDS.correctAnswer:
      return row.correctAnswer ?? '';
    case QUESTION_SORT_FIELDS.mimeType:
      return row.mimeType ?? '';
    case QUESTION_SORT_FIELDS.questionType:
      return row.questionType ?? '';
    case QUESTION_SORT_FIELDS.active:
      return row.active ?? 0;
    case QUESTION_SORT_FIELDS.question:
    default:
      return row.question ?? '';
  }
}

/** Sorts the current page of question rows in memory. */
export function sortQuestionsLocally(rows, sortBy, sortDir) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const dir = sortDir === 'desc' ? -1 : 1;
  const field = sortBy || QUESTION_LIST_DEFAULTS.sortBy;

  list.sort((a, b) => {
    const av = questionSortValue(a, field);
    const bv = questionSortValue(b, field);
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
  });

  return list;
}

/**
 * Parses paginated question list API response; falls back to array payloads for legacy callers.
 */
export function parseQuestionListResponse(payload) {
  if (Array.isArray(payload)) {
    const content = payload.map(mapQuestionApiRow);
    return {
      content,
      totalElements: content.length,
      totalPages: 1,
      page: 0,
      size: content.length,
    };
  }

  const raw = Array.isArray(payload?.content) ? payload.content : [];
  return {
    content: raw.map(mapQuestionApiRow),
    totalElements: Number(payload?.totalElements ?? 0),
    totalPages: Number(payload?.totalPages ?? 0),
    page: Number(payload?.page ?? 0),
    size: Number(payload?.size ?? 10),
  };
}
